import { useMemo, useState } from 'react';
import SectionBox from '../../components/SectionBox';
import { useUtComparisonElecStore } from '../store/useUtComparisonElecStore';
import { ElecDiffRow, ElecSourceRow } from '../types';
import { getRowVals } from '../utils/compareElec';
import BreakerDetailModal from './BreakerDetailModal';

const CARD_PALETTE = ['bh1', 'bh2', 'bh3', 'bh4', 'bh5', 'bh6'];

const breakerMap = (rows: ElecSourceRow[], vt: string) => {
  const m = new Map<number, number>();
  for (const r of rows) {
    if (r.vt !== vt || r.brkeCapa == null) continue;
    m.set(r.brkeCapa, (m.get(r.brkeCapa) ?? 0) + r.elpwEqpQty);
  }
  return m;
};

const breakerMapAll = (rows: ElecSourceRow[]) => {
  const m = new Map<number, number>();
  for (const r of rows) {
    if (r.brkeCapa == null) continue;
    m.set(r.brkeCapa, (m.get(r.brkeCapa) ?? 0) + r.elpwEqpQty);
  }
  return m;
};

const BreakerCard = ({
  title,
  cardCls,
  before,
  after,
  onClickBrk,
}: {
  title: string;
  cardCls: string;
  before: Map<number, number>;
  after: Map<number, number>;
  onClickBrk: (brk: number) => void;
}) => {
  // target이 es5인데 downlevelIteration이 꺼져 있어 Map 이터레이터는 스프레드로 못 편다(TS2802)
  const brkCapas = Array.from(before.keys()).concat(Array.from(after.keys()));
  const keys = Array.from(new Set(brkCapas)).sort((a, b) => a - b);
  const totalB = Array.from(before.values()).reduce((s, v) => s + v, 0);
  const totalA = Array.from(after.values()).reduce((s, v) => s + v, 0);
  const totalDiff = totalA - totalB;
  return (
    <div className="brk-card">
      <div className={`brk-card-hdr ${cardCls}`}>{title}</div>
      <table className="brk-tbl">
        <thead>
          <tr>
            <th>용량</th>
            <th>전</th>
            <th>후</th>
            <th>증감</th>
          </tr>
        </thead>
        <tbody>
          {keys.map((brk) => {
            const qb = before.get(brk) ?? 0;
            const qa = after.get(brk) ?? 0;
            const d = qa - qb;
            return (
              <tr key={brk}>
                <td className="brk-click" onClick={() => onClickBrk(brk)}>
                  {brk}A
                </td>
                <td>{qb || '-'}</td>
                <td>{qa || '-'}</td>
                <td className={d > 0 ? 'diff-p' : d < 0 ? 'diff-n' : ''}>
                  {d > 0 ? '+' : ''}
                  {d || '-'}
                </td>
              </tr>
            );
          })}
          <tr className="brk-total">
            <td>합계</td>
            <td>{totalB}</td>
            <td>{totalA}</td>
            <td className={totalDiff > 0 ? 'diff-p' : totalDiff < 0 ? 'diff-n' : ''}>
              {totalDiff > 0 ? '+' : ''}
              {totalDiff}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

/** ③ 차단기 현황 — 설비군 → Line 필터 + 전압별/전체 카드, 용량 클릭 시 상세 팝업 */
const BreakerSection = () => {
  const sourceBefore = useUtComparisonElecStore((s) => s.sourceBefore);
  const sourceAfter = useUtComparisonElecStore((s) => s.sourceAfter);
  const diffRows = useUtComparisonElecStore((s) => s.diffRows);

  const [grpFilter, setGrpFilter] = useState('전체');
  const [lineFilter, setLineFilter] = useState('전체');
  const [modalTarget, setModalTarget] = useState<{ vt: string; brk: number } | null>(null);

  const allGrps = useMemo(
    () =>
      Array.from(new Set([...sourceBefore, ...sourceAfter].map((r) => r.eqclNm)))
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b, 'ko')),
    [sourceBefore, sourceAfter]
  );

  const allVts = useMemo(
    () =>
      Array.from(new Set([...sourceBefore, ...sourceAfter].map((r) => r.vt)))
        .filter((v) => v && v !== 'UNKNOWN')
        .sort((a, b) => parseFloat(a) - parseFloat(b) || a.localeCompare(b)),
    [sourceBefore, sourceAfter]
  );

  const grpFiltered = (rows: ElecSourceRow[]) =>
    grpFilter === '전체' ? rows : rows.filter((r) => r.eqclNm === grpFilter);

  const allLines = useMemo(() => {
    const b = grpFiltered(sourceBefore);
    const a = grpFiltered(sourceAfter);
    return Array.from(new Set([...b, ...a].map((r) => r.prdnLnNm)))
      .filter(Boolean)
      .sort((x, y) => x.localeCompare(y, 'ko', { numeric: true }));
  }, [sourceBefore, sourceAfter, grpFilter]);

  const brkBefore = useMemo(() => {
    let rows = grpFiltered(sourceBefore);
    if (lineFilter !== '전체') rows = rows.filter((r) => r.prdnLnNm === lineFilter);
    return rows;
  }, [sourceBefore, grpFilter, lineFilter]);
  const brkAfter = useMemo(() => {
    let rows = grpFiltered(sourceAfter);
    if (lineFilter !== '전체') rows = rows.filter((r) => r.prdnLnNm === lineFilter);
    return rows;
  }, [sourceAfter, grpFilter, lineFilter]);

  const matchingRows = (vt: string, brk: number): ElecDiffRow[] => {
    if (!diffRows) return [];
    return diffRows.filter((r) => {
      const b = r.before;
      const a = r.after;
      const brkM = b?.brkeCapa === brk || a?.brkeCapa === brk;
      const vtM = vt === 'ALL' || b?.vt === vt || a?.vt === vt;
      const grpM = grpFilter === '전체' || getRowVals(r).eqclNm === grpFilter;
      const lineM =
        lineFilter === '전체' ||
        getRowVals(r).lnBf === lineFilter ||
        getRowVals(r).lnAf === lineFilter;
      return brkM && vtM && grpM && lineM;
    });
  };

  if (sourceBefore.length === 0 && sourceAfter.length === 0) return null;

  return (
    <SectionBox no="③" title="차단기 현황" note="설비군 → Line 필터 선택 후 용량 클릭 시 상세 팝업">
      <div className="brk-filter-bar">
        <span>설비군:</span>
        {['전체', ...allGrps].map((g) => (
          <button
            key={g}
            type="button"
            className={`grp-btn${g === grpFilter ? ' active' : ''}`}
            onClick={() => {
              setGrpFilter(g);
              setLineFilter('전체');
            }}
          >
            {g}
          </button>
        ))}
      </div>
      <div className="brk-line-filter-bar">
        <span>Line:</span>
        {['전체', ...allLines].map((l) => (
          <button
            key={l}
            type="button"
            className={`line-btn${l === lineFilter ? ' active' : ''}`}
            onClick={() => setLineFilter(l)}
          >
            {l}
          </button>
        ))}
      </div>
      <div className="brk-grid">
        {allVts.map((vt, i) => (
          <BreakerCard
            key={vt}
            title={vt}
            cardCls={CARD_PALETTE[i % CARD_PALETTE.length]}
            before={breakerMap(brkBefore, vt)}
            after={breakerMap(brkAfter, vt)}
            onClickBrk={(brk) => setModalTarget({ vt, brk })}
          />
        ))}
        <BreakerCard
          title={`합계 (전압 ${allVts.length}종)`}
          cardCls="bh-tot"
          before={breakerMapAll(brkBefore)}
          after={breakerMapAll(brkAfter)}
          onClickBrk={(brk) => setModalTarget({ vt: 'ALL', brk })}
        />
      </div>
      {modalTarget && (
        <BreakerDetailModal
          title={`차단기 ${modalTarget.brk}A | ${
            modalTarget.vt === 'ALL' ? '전압 전체' : modalTarget.vt
          }${grpFilter !== '전체' ? ` | 설비군: ${grpFilter}` : ''}${
            lineFilter !== '전체' ? ` | Line: ${lineFilter}` : ''
          }`}
          rows={matchingRows(modalTarget.vt, modalTarget.brk)}
          onClose={() => setModalTarget(null)}
        />
      )}
    </SectionBox>
  );
};

export default BreakerSection;
